import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faEye, faHeart, faStar, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faThumbsUp as faThumbsUpFilled } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from "../../../utilities/formatDate";
import './discoverStories.css';
import { DisplayGenreIcon } from "../../../utilities/displayGenreIcon";
import JsonToReact from "../../../utilities/jsonToReact";
import { DiscoverGenreSelector } from "./discoverGenreSelector";
import { useDispatch, useSelector } from "react-redux";
import { selectDiscoverGenre } from "./discoverSlice";
import { createFetchMoreData, setStories } from "../../../app/appSlice";

export const DiscoverStories = () => {
    const stories = useSelector((state) => state.app.stories);
    const [filteredStories, setFilteredStories] = useState([]);
    const [activeView, setActiveView] = useState('random');
    const [expandedStories, setExpandedStories] = useState({ 0: true }); // Start with first story expanded
    const navigate = useNavigate();
    const [isFetching, setIsFetching] = useState(false);
    const selectedGenre = useSelector(selectDiscoverGenre);
    const genre = selectedGenre[0];
    const dispatch = useDispatch();
    
    useEffect(() => {
        if (stories?.length > 0) {
            filterStories(activeView, stories, genre);
        }
    }, [stories, activeView, genre]);
    


    const filterStories = (view, stories, genre) => {
        let filtered = [...stories];
    
        if (genre?.length > 0) {
            filtered = filtered.filter(story => story.genres.includes(genre));
        }
    
        if (view === 'random') {
            filtered = filtered.sort(() => Math.random() - 0.5).slice(0, 10);
        } else if (view === 'recent') {
            filtered = filtered.sort((a, b) => new Date(b.time) - new Date(a.time));
        } else if (view === 'popular') {
            filtered = filtered.sort((a, b) => b.influence - a.influence).slice(0, 10);
        }
    
        setFilteredStories(filtered);
    };
    

    const handleNav = (view) => {
        setActiveView(view);
    };


    const handleStoryClick = (story) => {
        navigate(`/${story.user}/${story.uid}`);
    };

    const toggleExpand = (index) => {
        setExpandedStories(prevState => ({
            ...prevState,
            [index]: !prevState[index]
        }));
    };

    const fetchMoreStories = createFetchMoreData("stories", setStories);

    const handleFetch = () => {
        if (isFetching) return;
        setIsFetching(true);
        const filters = genre ? [{ field: "genres", op: "array-contains", value: genre }] : [];
        const orderByMap = {
          recent: { field: "time", direction: "desc" },
          popular: { field: "influence", direction: "desc" },
        };
      
        if (activeView === "random") {
            dispatch(fetchMoreStories({ filters, pageSize: 1 })).then((fetched) => {
              if (fetched?.length) {
                dispatch(setStories({ data: fetched, replace: false }));

              }
              setIsFetching(false);
            });
          } else {
          dispatch(fetchMoreStories({
            filters,
            orderBy: orderByMap[activeView],
            pageSize: 1,
          })).then(() => {
            setIsFetching(false); // ✅ Wait for dispatch to resolve
          });
        }
    };
      


   
   

    return (
        <div className='discover-stories-page'>
            
            <nav className="discover-nav">
                <div className={activeView === 'random' ? "active" : ""} onClick={() => handleNav('random')}>Random</div>
                <div className={activeView === 'popular' ? "active" : ""} onClick={() => handleNav('popular')}>Popular</div>
                <div className={activeView === 'recent' ? "active" : ""} onClick={() => handleNav('recent')}>Recent</div>
            </nav>
            <div className='discover-genre-selector'>
            <DiscoverGenreSelector iconStyle={{ width: "70px" }} />
            </div>

            <div className='discover-list'>
                {filteredStories.map((story, index) => (
                    <div key={story.uid || index} className='discover-story'>
                        <div className='discover-story-details' onClick={() => handleStoryClick(story)}>
                            <div className="title-author-genres">
                                <span className="title-author">
                                    <strong className='storyTitle'>{story.title}</strong><br/>
                                    <strong className="story-author">{story.author}</strong>
                                </span>
                                <div className="icons-chevron">
                                    <DisplayGenreIcon genres={story.genres} style ={{width: '2rem', borderRadius: '2rem', marginLeft: '.5rem'}}/>
                                    <span onClick={(e) => {e.stopPropagation(); toggleExpand(index);}} className="toggle-btn">
                                        <FontAwesomeIcon icon={expandedStories[index] ? faChevronUp : faChevronDown} />
                                    </span>
                                </div>
                            </div>

                            {expandedStories[index] && (
                                <div className="story-details-prompt">
                                    {JsonToReact({ jsonString: story.prompt, omitSpan: true })}
                                </div>
                            )}

                            {expandedStories[index] && (
                                <p className="discover-snippet">
                                    <i>{story.story.substring(0, 240)}...</i>
                                </p>
                            )}
                        </div>

                        <div className='story-interaction'>
                            <div>
                                <span><FontAwesomeIcon id='thumb' icon={faThumbsUpFilled} /> {story.likes ? story.likes.length : 0}</span>
                                {story.rateable && <span><FontAwesomeIcon id='star' icon={faStar} /> {story.ratings ? (story.ratings.reduce((sum, r) => sum + r.rating, 0) / story.ratings.length).toFixed(1) : 0} ({story.ratings ? story.ratings.length : 0})</span>}
                                <span><FontAwesomeIcon id='comment' icon={faComment} /> {story.comments ? story.comments.length : 0}</span>
                                <span><FontAwesomeIcon id='heart' icon={faHeart} /> {story.favorites ? story.favorites.length : 0}</span>
                                <span><FontAwesomeIcon id='eye' icon={faEye} /> {story.views ?? 0}</span>
                            </div>
                            <div>
                                <span>{formatDate(story.time)}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {isFetching && <div className="mini-loading-screen">
                <img src="/images/brand/favlogotrans.png" alt="Loading..." className="mini-loading-image" />
            </div>}
            {!isFetching && <button className="fetchDataButton" onClick={()=> handleFetch()}>Load more Stories</button>}
        </div>
    );
};
